import { useState, useEffect } from 'react';
import { Flame, Calendar } from 'lucide-react';
import { apiCall } from './api';
import { getT } from './i18n';

/**
 * Streak Calendar — affiche les 28 derniers jours sous forme de heatmap.
 * Intensité de la couleur proportionnelle au XP gagné dans la journée.
 */
export default function StreakCalendar({ token, lang = 'fr' }) {
  const t = getT(lang);
  const [data, setData] = useState(null);

  useEffect(() => {
    apiCall('/streak/history?days=28', 'GET', null, token).then(setData).catch(() => {});
  }, [token]);

  if (!data) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
        <div className="text-xs text-white/40">{t('dash_loading')}</div>
      </div>
    );
  }

  const maxXp = Math.max(10, ...data.days.map(d => d.xp));
  const intensity = (xp) => {
    if (xp <= 0) return 'bg-white/5 border-white/10';
    const r = xp / maxXp;
    if (r < 0.25) return 'bg-emerald-500/25 border-emerald-500/40';
    if (r < 0.50) return 'bg-emerald-500/50 border-emerald-400/60';
    if (r < 0.75) return 'bg-emerald-400/70 border-emerald-300/70';
    return 'bg-emerald-300 border-emerald-200';
  };

  const dayLabel = (iso) => new Date(iso + 'T12:00:00Z').toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', { weekday: 'short', day: '2-digit' });

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid="streak-calendar-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2"><Calendar size={18} className="text-orange-300" /><h3 className="font-bold">{t('sc_title')}</h3></div>
        <div className="flex items-center gap-1 text-xs">
          <Flame size={14} className="text-orange-400" />
          <span className="font-bold text-orange-300">{data.current_streak}</span>
          <span className="text-white/60">{lang === 'fr' ? 'j' : 'd'}</span>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1" data-testid="streak-cells">
        {data.days.map((d, i) => (
          <div key={d.date} title={`${dayLabel(d.date)} — ${d.xp} XP`}
            className={`aspect-square rounded border ${intensity(d.xp)}`}
            data-testid={`streak-cell-${i}`} />
        ))}
      </div>

      <div className="flex items-center justify-between mt-3 text-[10px] text-white/40">
        <span>{t('sc_less')}</span>
        <div className="flex gap-0.5">
          <div className="w-3 h-3 rounded-sm bg-white/5 border border-white/10" />
          <div className="w-3 h-3 rounded-sm bg-emerald-500/25 border border-emerald-500/40" />
          <div className="w-3 h-3 rounded-sm bg-emerald-500/50 border border-emerald-400/60" />
          <div className="w-3 h-3 rounded-sm bg-emerald-400/70 border border-emerald-300/70" />
          <div className="w-3 h-3 rounded-sm bg-emerald-300 border border-emerald-200" />
        </div>
        <span>{t('sc_more')}</span>
      </div>

      <div className="mt-3 text-[11px] text-white/60 flex justify-between">
        <span>{t('sc_active_days')}</span>
        <span className="font-bold text-emerald-300">{data.total_active_days}/28</span>
      </div>
    </div>
  );
}
